import DocumentHead from '@/components/DocumentHead';
import Footer from '@/components/Footer';
import createTheme from '@orgnc/core/lib/themes/createTheme';
import { MultiTheme, getTheme } from '@orgnc/core/lib/themes/multiTheme';
// import Header from '@/components/Header';
import HtlAd from '@/components/blocks/htl-ad/htl-ad';
import ArticleTemplate from '@/components/templates/Article/ArticleTemplate';
import bmgSettingsQuery from '@/lib/queries/bmgSettingsQuery';
import HtlAdBlockFragment from '@/lib/queries/htlAdBlockFragment';
import htlSettingsQuery from '@/lib/queries/htlSettingsQuery';
import podcastSectionFragment from '@/lib/queries/podcastSectionFragment';
import elementsTheme from '@orgnc/core/components/themes/Default';
import { getArticleTemplateByName } from '@orgnc/core/lib/themes/templates/article';
import getArticleTemplate from '@orgnc/core/lib/themes/templates/functions/getArticleTemplate';
import getCategoryTemplate from '@orgnc/core/lib/themes/templates/functions/getCategoryTemplate';
import { GetPageTemplate } from '@orgnc/core/lib/themes/templates/functions/getPageTemplate';
import { getPageTemplateByName } from '@orgnc/core/lib/themes/templates/page';
import { defaultThemeSettings } from '@orgnc/core/lib/wordpress/api/adapters/themeSettingsAdapter';
import postCommentsQuery from '@orgnc/core/lib/wordpress/api/queries/postCommentsQuery';
import dynamic from 'next/dynamic';
import AudienceEmbedBlock from './components/blocks/audience-embed/audience-embed-block';
import CoreEmbed from './components/blocks/core-embed/core-embed';
import CoreGroupBlock from './components/blocks/core-group/core-group-block';
import CoreImageBlock from './components/blocks/core-image/core-image-block';
import CoreMediaText from './components/blocks/core-media-text/core-media-text';
import FacebookEmbedBlock from './components/blocks/facebook-embed/facebook-embed-block';
// import STNEmbedBlock from './components/blocks/stn-embed/stn-embed';
import TockifyEmbedBlock from './components/blocks/tockify-embed/tockify-embed';
import VoteEIBlock from './components/blocks/vote-ei/vote-ei';
import GettyImages from './components/GettyImages';
import EvvntHtmlBlock from './components/EvvntHtmlBlock';
import GlobalScripts from './components/GlobalScripts';
import Header from './components/header/header';
import PodcastSection from './components/PodcastSection';
import ContestsPostTemplate from './components/templates/Article/ContestsPostTemplate';
import CategoryTemplate from './components/templates/Category/CategoryTemplate';
import HomePageTemplate from './components/templates/Page/HomePageTemplate';
import PageTemplate from './components/templates/Page/PageTemplate';
import getStreams from './lib/queries/getStreams';
import AdvertisePageTemplate from './components/templates/Page/AdvertisePageTemplate';
import advertiseTemplateDetailsQuery from './lib/queries/advertiseTemplateDetailsQuery';

import {
  augustatodayTheme,
  b1027VGSTheme,
  b1077thebounceTheme,
  backstagecountryTheme,
  checkpointxpTheme,
  coyotecountrylvTheme,
  daveandchuckthefreakTheme,
  espnswflTheme,
  foxy99Theme,
  hd983Theme,
  hotaugustaTheme,
  ilovebobfmTheme,
  kicks99Theme,
  magic983Theme,
  musthavesandfunfindsTheme,
  mykissradioTheme,
  playjackradioTheme,
  podcastradiousTheme,
  roardetroitTheme,
  sunny1027Theme,
  sunny943Theme,
  theandiesummersshowTheme,
  wdhafmTheme,
  wgacTheme,
  wjrzTheme,
  wkmlTheme,
  wmtramTheme,
  wratTheme,
  wrifTheme,
  wtmrradioTheme,
  wwdbamTheme,
} from './themes/sites';
import { SiteType } from './themes/type';

const DailyMotionEmbed = dynamic(
  () =>
    import(
      '@orgnc/core/components/blocks/CoreEmbedBlock/EmbedType/DailyMotionEmbedBlock'
    )
);

const DEFAULT_TEMPLATE_NAME = 'default';

const baseTheme = createTheme({
  ...elementsTheme,
  // It allows to specify the 'Head' component to use for the Next.js application's document structure.
  _document: {
    Head: DocumentHead,
  },
  Header,
  Footer,
  muiTheme: espnswflTheme,
  graphqlClient: {
    useMultiTenantConfig: true,
  },
  redisCache: {
    useMultiTenantConfig: true,
  },
  Global: GlobalScripts,
  extend: {
    fragments: {
      // settings: extendSettingsFragment,
    },
    blocks: [
      {
        name: 'refact/fb-embed',
        component: FacebookEmbedBlock,
      },
      {
        name: 'refact/audience-embed',
        component: AudienceEmbedBlock,
      },
      {
        name: 'acf/htl-ad',
        component: HtlAd,
        fragment: HtlAdBlockFragment,
      },
      {
        name: 'create-block/bmg-general-blocks',
        component: PodcastSection,
        fragment: podcastSectionFragment,
      },
      {
        name: 'acf/vote-ei-block',
        component: VoteEIBlock,
      },
      {
        name: 'core/image',
        component: CoreImageBlock,
      },
      {
        name: 'core/media-text',
        component: CoreMediaText,
      },
      // deprecated
      {
        name: 'core/embed',
        component: CoreEmbed,
      },
      {
        name: 'core/group',
        component: CoreGroupBlock,
      },
      {
        name: 'create-block/bmg-getty-images-block',
        component: GettyImages,
      },
      {
        name: 'acf/tockify-embed',
        component: TockifyEmbedBlock,
      },
      {
        name: 'create-block/bmg-evvnt-html-block',
        component: EvvntHtmlBlock,
      },
    ],
    queries: {
      global: [
        htlSettingsQuery,
        bmgSettingsQuery,
        getStreams,
        advertiseTemplateDetailsQuery,
      ],
    },
  },
  options: {
    CoreEmbedBlock: {
      providers: {
        dailymotion: DailyMotionEmbed,
      },
    },
  },
  pageTypes: {
    // Default to the base page types
    ...elementsTheme.pageTypes,

    // But instead of full width, use the "small" article template variant
    article: {
      templates: [
        {
          name: DEFAULT_TEMPLATE_NAME,
          component: ArticleTemplate,
          options: {
            variant: 'small',
          },
          queries: [postCommentsQuery],
        },
        {
          name: 'contests',
          component: ContestsPostTemplate,
          options: {
            variant: 'small',
          },
        },
      ],
      getTemplate: (article) => {
        if (
          article.categories.filter((item) => item.slug === 'contests').length
        ) {
          return getArticleTemplateByName(theme, 'contests');
        }
        return getArticleTemplate(article, theme);
      },
    },

    category: {
      templates: [
        {
          name: DEFAULT_TEMPLATE_NAME,
          component: CategoryTemplate,
        },
      ],
      getTemplate: (category) => {
        return getCategoryTemplate(category, defaultThemeSettings, theme);
      },
    },

    // And use the custom, simplified template for the root URL ("/")
    page: {
      templates: [
        { name: 'default', component: PageTemplate },
        { name: 'Homepage', component: HomePageTemplate },
        { name: 'Advertise', component: AdvertisePageTemplate },
      ],
      getTemplate: ((page) => {
        if (page.uri === '/') {
          return getPageTemplateByName(theme, 'Homepage');
        }
        if (page.uri === '/advertise/') {
          return getPageTemplateByName(theme, 'Advertise');
        }
        return getPageTemplateByName(theme, DEFAULT_TEMPLATE_NAME);
      }) as GetPageTemplate,
    },
  },
});

export const multiTheme = {
  base: baseTheme,
  variations: {
    [SiteType.ESPNSWFL]: {
      muiTheme: espnswflTheme,
    },
    [SiteType.B1027VGS]: {
      muiTheme: b1027VGSTheme,
    },
    [SiteType.AUGUSTATODAY]: {
      muiTheme: augustatodayTheme,
    },
    [SiteType.COYOTECOUNTRYLV]: {
      muiTheme: coyotecountrylvTheme,
    },
    [SiteType.FOXY99]: {
      muiTheme: foxy99Theme,
    },
    [SiteType.WKML]: {
      muiTheme: wkmlTheme,
    },
    [SiteType.PLAYJACKRADIO]: {
      muiTheme: playjackradioTheme,
    },
    [SiteType.B1077THEBOUNCE]: {
      muiTheme: b1077thebounceTheme,
    },
    [SiteType.MYKISSRADIO]: {
      muiTheme: mykissradioTheme,
    },
    [SiteType.SUNNY943]: {
      muiTheme: sunny943Theme,
    },
    [SiteType.WRAT]: {
      muiTheme: wratTheme,
    },
    [SiteType.MAGIC983]: {
      muiTheme: magic983Theme,
    },
    [SiteType.WDHAFM]: {
      muiTheme: wdhafmTheme,
    },
    [SiteType.WJRZ]: {
      muiTheme: wjrzTheme,
    },
    [SiteType.WMTRAM]: {
      muiTheme: wmtramTheme,
    },
    [SiteType.WTMRRADIO]: {
      muiTheme: wtmrradioTheme,
    },
    [SiteType.WGAC]: {
      muiTheme: wgacTheme,
    },
    [SiteType.KICKS99]: {
      muiTheme: kicks99Theme,
    },
    [SiteType.HD983]: {
      muiTheme: hd983Theme,
    },
    [SiteType.HOTAUGUSTA]: {
      muiTheme: hotaugustaTheme,
    },
    [SiteType.SUNNY1027]: {
      muiTheme: sunny1027Theme,
    },
    [SiteType.ILOVEBOBFM]: {
      muiTheme: ilovebobfmTheme,
    },
    [SiteType.WRIF]: {
      muiTheme: wrifTheme,
    },
    [SiteType.BACKSTAGECOUNTRY]: {
      muiTheme: backstagecountryTheme,
    },
    [SiteType.CHECKPOINTXP]: {
      muiTheme: checkpointxpTheme,
    },
    [SiteType.DAVEANDCHUCKTHEFREAK]: {
      muiTheme: daveandchuckthefreakTheme,
    },
    [SiteType.ROARDETROIT]: {
      muiTheme: roardetroitTheme,
    },
    [SiteType.PODCASTRADIOUS]: {
      muiTheme: podcastradiousTheme,
    },
    [SiteType.THEANDIESUMMERSSHOW]: {
      muiTheme: theandiesummersshowTheme,
    },
    [SiteType.MUSTHAVESANDFUNFINDS]: {
      muiTheme: musthavesandfunfindsTheme,
    },
    [SiteType.WWDBAM]: {
      muiTheme: wwdbamTheme,
    },
    [SiteType.X1075LASVEGAS]: {},
    [SiteType.WROR]: {},
    [SiteType.WMGK]: {},
    [SiteType.WMMR]: {},
    [SiteType.WILD941]: {},
    [SiteType.WJBR]: {},
    [SiteType.V1019]: {},
    [SiteType.WCSX]: {},
    [SiteType.THEBOUNCESWFL]: {},
    [SiteType.ROCK929ROCKS]: {},
    [SiteType.SUNNY1063]: {},
    [SiteType.PLAYATAMPA]: {},
    [SiteType.POWER98FM]: {},
    [SiteType.MYQ105]: {},
    [SiteType.PLAYA993]: {},
    [SiteType.KISS951]: {},
    [SiteType.KISSFMDETROIT]: {},
    [SiteType.K1047]: {},
    [SiteType.GETUPCREWSHOW]: {},
    [SiteType.HOT969BOSTON]: {},
    [SiteType.FOXSPORTSRADIOCHARLOTTE]: {},
    [SiteType.FOXSPORTSRADIONEWJERSEY]: {},
    [SiteType.DETROITPRAISENETWORK]: {},
    [SiteType.COUNTRY1025]: {},
    [SiteType.COUNTRY1037FM]: {},
    [SiteType.B995QYK]: {},
    [SiteType.B1039]: {},
    [SiteType.B96KROCK]: {},
    [SiteType.B975THEFANATIC]: {},
    [SiteType.B985THESPORTSHUB]: {},
    [SiteType.B987THESHARK]: {},
    [SiteType.B925MAXIMA]: {},
    [SiteType.B925XTU]: {},
    [SiteType.B957BENFM]: {},
    [SiteType.B963KKLZ]: {},
    [SiteType.B1057MAXIMA]: {},
    [SiteType.B1051THEBOUNCE]: {},
    [SiteType.CHARLOTTENEWSROOM]: {},
  },
} as MultiTheme<SiteType>;

const theme = getTheme(SiteType.ESPNSWFL, multiTheme);

export default theme;
